<script setup lang="ts">
import useConsumerProducts from "@/consumer/composables/useConsumerProducts";
import useConsumerProductGirerdSurvey from "@/consumer/composables/useConsumerProductGirerdSurvey";
import {computed, ref} from "vue";
import {ConsumerProduct} from "@/consumer/interfaces";
import BaseContainer from "@/core/components/base/BaseContainer.vue";
import BaseFlexSpinner from "@/core/components/base/spinner/BaseFlexSpinner.vue";
import ConsumerProductTrackingHeader
    from "@/interview/components/instance-modal/consumer-product-tracking/ConsumerProductTrackingHeader.vue";
import ConsumerProductTreatments
    from "@/interview/components/instance-modal/consumer-product-tracking/treatments/ConsumerProductTreatments.vue";
import ConsumerProductDosageDialog from "@/drug-interaction/components/dosage/ConsumerProductDosageDialog.vue";
import ConsumerProductInteractionsDialog
    from "@/drug-interaction/components/alerts/ConsumerProductInteractionsDialog.vue";
import ConsumerKeyService from "@/consumer/services/ConsumerKeyService";

const {consumerKey} = defineProps<{ consumerKey: string; }>();
const dosageDialogOpen = ref(false);
const alertsDialogOpen = ref(false);

const {data, isLoading, meta, isValidating, mutate} = useConsumerProducts(consumerKey);
const {
    survey,
    latestInstance,
    isLoading: isLoadingSurvey,
    mutate: mutateSurvey
} = useConsumerProductGirerdSurvey(consumerKey);
const selectedProduct = ref<ConsumerProduct | null>(null);

const customerId = computed(() => {
    return ConsumerKeyService.customerIdFromKey(consumerKey);
});

/**
 * Handle the opening of the dosage dialog for a specific product.
 */
const handleOpenDosage = (product: ConsumerProduct) => {
    selectedProduct.value = product;
    dosageDialogOpen.value = true;
};

/**
 * Compute the dosages used in the dosage dialog.
 *
 * if a product is selected, it is added at the beginning of the list.
 * Only products without latest dosage are included.
 */
const dosageProducts = computed(() => {
    if (!data.value) {
        return [];
    }

    let products = data.value.map((consumerProducts) => consumerProducts.product);

    if (selectedProduct.value) {
        if (!selectedProduct.value.product.latest_dosage) {
            products = products.filter(_product => !_product.latest_dosage);
        }

        return [selectedProduct.value.product, ...products.filter(p => p.id !== selectedProduct.value!.product.id)];
    }

    return products;
});

/**
 * When dosage dialog is close, reset the selected product and refresh the data.
 */
const handleDosageOpenChange = (open: boolean) => {
    if (!open) {
        selectedProduct.value = null;
        mutate();
    }
};
</script>

<template>
  <BaseContainer v-if="consumerKey" class="pb-4">
    <BaseFlexSpinner v-if="isLoading || isValidating || isLoadingSurvey" />
    <div v-else-if="!customerId">
      <p>L'analyse pharmaceutique est seulement disponible pour les assurés.</p>
    </div>
    <div v-else-if="data && survey" class="flex flex-col gap-4">
      <ConsumerProductTrackingHeader
        :consumer-key="consumerKey"
        :products="data"
        :girerd="{
          surveyId: survey.id,
          latestInstance: latestInstance?.completed_at ? (new Date(latestInstance.completed_at)) : null
        }"
        :alert-review="{
          done: meta?.alert_review_done ?? false,
          reviewDate: meta?.alert_review_date ? new Date(meta.alert_review_date) : null
        }"
        @open-dosage-dialog="dosageDialogOpen = true"
        @girerd-closed="mutateSurvey(); mutate();"
        @open-alerts-dialog="alertsDialogOpen = true"
      />
      <div class="px-4">
        <ConsumerProductTreatments :consumer-products="data" @open-dosage-dialog="handleOpenDosage" />
      </div>
      <ConsumerProductDosageDialog
        v-if="dosageProducts?.length"
        v-model:open="dosageDialogOpen"
        :products="dosageProducts"
        :consumer-key="consumerKey"
        @update:open="handleDosageOpenChange"
      />
      <ConsumerProductInteractionsDialog
        v-model:open="alertsDialogOpen"
        :products="data"
        :customer-id="customerId"
        @saved="alertsDialogOpen = false; mutate()"
      />
    </div>
    <div v-else class="text-center">
      <p>Un problème est survenu</p>
    </div>
  </BaseContainer>
</template>
